import type { IAdViewManaged } from "@finn-no/adview-provider-frontend-types";
import type {
  AgricultureAd,
  AgricultureVehicleAd,
  ATractorAd,
  BoatAd,
  BoatRentAd,
  BoatSaleAd,
  BusAd,
  CarAd,
  CaravanAd,
  ConstructionAd,
  MobileHomeAd,
  MobilityAd,
  MotorbikeAd,
  TruckAd,
} from "@mobility-item-page/types";

export const isOnMotorMarket = (adViewType?: string) => {
  return (
    adViewType?.startsWith("car-") ||
    adViewType?.startsWith("b2b") ||
    adViewType?.startsWith("motorcycle") ||
    adViewType?.startsWith("moped") ||
    adViewType?.startsWith("atv") ||
    adViewType?.startsWith("snowmobile") ||
    adViewType?.startsWith("boat") ||
    adViewType?.startsWith("agriculture") ||
    adViewType?.startsWith("mobile-home") ||
    adViewType?.startsWith("caravan") ||
    adViewType?.startsWith("truck") ||
    adViewType?.startsWith("construction") ||
    adViewType?.startsWith("bus") ||
    adViewType?.startsWith("a-tractor")
  );
};

export const isCarAd = (ad: MobilityAd): ad is CarAd => {
  return isCar(ad.adViewType);
};

const isCar = (adViewType?: MobilityAd["adViewType"]) => {
  const carSchemas = ["car-used", "car-new", "car-used-business", "car-leasing-business", "car-leasing"];
  return carSchemas.includes(adViewType || "");
};

export const isLeasing = (adViewType?: MobilityAd["adViewType"]) => {
  const leasingSchemas = ["car-leasing-business", "car-leasing"];
  return leasingSchemas.includes(adViewType || "");
};

export const isBoatAd = (ad: MobilityAd): ad is BoatAd => {
  return isBoat(ad.adViewType);
};

const isBoat = (adViewType?: MobilityAd["adViewType"]) => {
  const boatSchemas = ["boat-dock", "boat-motor", "boat-new-sale", "boat-rent", "boat-sale", "boat-wanted"];
  return boatSchemas.includes(adViewType || "");
};

export const isMotorbikeAd = (ad: MobilityAd): ad is MotorbikeAd => {
  return isMotorbike(ad.adViewType);
};

const isMotorbike = (adViewType?: MobilityAd["adViewType"]) => {
  const motorcycleSchemas = ["motorcycle", "moped", "atv", "snowmobile"];
  return motorcycleSchemas.includes(adViewType || "");
};

export const isAgricultureAd = (ad: MobilityAd): ad is AgricultureAd => {
  return isAgriculture(ad.adViewType);
};

const isAgriculture = (adViewType?: MobilityAd["adViewType"]) => {
  const agricultureSchemas = ["agriculture-tractor", "agriculture-thresher", "agriculture-tools"];
  return agricultureSchemas.includes(adViewType || "");
};

export const isAgricultureVehicleAd = (ad: MobilityAd): ad is AgricultureVehicleAd => {
  return isAgricultureVehicle(ad.adViewType);
};

const isAgricultureVehicle = (adViewType?: MobilityAd["adViewType"]) => {
  const agricultureVehicleSchemas = ["agriculture-tractor", "agriculture-thresher"];
  return agricultureVehicleSchemas.includes(adViewType || "");
};

export const isTruckAd = (ad: MobilityAd): ad is TruckAd => {
  return isTruck(ad.adViewType);
};

const isTruck = (adViewType?: MobilityAd["adViewType"]) => {
  const truckSchemas = ["truck"];
  return truckSchemas.includes(adViewType || "");
};

export const isConstructionAd = (ad: MobilityAd): ad is ConstructionAd => {
  return isConstruction(ad.adViewType);
};

const isConstruction = (adViewType?: MobilityAd["adViewType"]) => {
  const constructionSchemas = ["construction"];
  return constructionSchemas.includes(adViewType || "");
};

const isBus = (adViewType?: MobilityAd["adViewType"]) => {
  const busSchemas = ["bus"];
  return busSchemas.includes(adViewType || "");
};

export const isBusAd = (ad: MobilityAd): ad is BusAd => {
  return isBus(ad.adViewType);
};

export const isMobileHomeAd = (ad: MobilityAd): ad is MobileHomeAd => {
  return isMobileHome(ad.adViewType);
};

const isMobileHome = (adViewType?: MobilityAd["adViewType"]) => {
  const mobileHomeSchemas = ["mobile-home"];
  return mobileHomeSchemas.includes(adViewType || "");
};

export const isCaravanAd = (ad: MobilityAd): ad is CaravanAd => {
  return isCaravan(ad.adViewType);
};

const isCaravan = (adViewType?: MobilityAd["adViewType"]) => {
  const caravanHomeSchemas = ["caravan"];
  return caravanHomeSchemas.includes(adViewType || "");
};

const isATractor = (adViewType?: MobilityAd["adViewType"]) => {
  const aTractorSchemas = ["a-tractor"];
  return aTractorSchemas.includes(adViewType || "");
};

export const isATractorAd = (ad: MobilityAd): ad is ATractorAd => {
  return isATractor(ad.adViewType);
};

export const isPrivateAdvertiser = (ownerUrn?: string) => {
  return getOwnerType(ownerUrn) === "user";
};

const isManagedField = (value: unknown): value is IAdViewManaged => {
  return (value as IAdViewManaged)?.value !== undefined;
};

// In case fields may change to managed fields we should use this function to get the value
export const getValue = <V>(value: V): string => {
  if (isManagedField(value)) {
    return value.value;
  }
  return value as string;
};

export const getId = <V>(value: V): number | undefined => {
  if (isManagedField(value)) {
    return value.id;
  }
};

export const isAuction = (salesForm?: IAdViewManaged) => {
  return salesForm && salesForm.id === 7;
};

export const isWantedForSale = (ad: BoatAd) => {
  if (ad.adViewType === "boat-wanted") {
    return true;
  }
  if (ad.adViewType !== "boat-motor") {
    return false;
  }
  return ad.trade_type?.id === 3;
};

export const isBoatRent = (ad: MobilityAd): ad is BoatRentAd => {
  return ad.adViewType === "boat-rent";
};

export const isBoatSale = (ad: MobilityAd): ad is BoatSaleAd => {
  return ["boat-sale", "boat-new-sale"].includes(ad.adViewType);
};

export const isForSale = (ad: MobilityAd) => {
  switch (ad.adViewType) {
    case "car-new":
    case "car-used":
    case "car-used-business":
    case "boat-sale":
    case "boat-new-sale":
    case "boat-motor":
    case "truck":
    case "construction":
    case "bus":
    case "agriculture-tractor":
    case "agriculture-thresher":
    case "agriculture-tools":
    case "moped":
    case "motorcycle":
    case "atv":
    case "snowmobile":
    case "a-tractor":
      return true;
    case "car-leasing":
    case "car-leasing-business":
    case "boat-rent":
    case "boat-wanted":
    case "boat-dock":
      return false;
    case "mobile-home":
    case "caravan":
      return ad.trade_type?.id === 1;
    default:
      return false;
  }
};

type OwnerType = "user" | "organisation";

export const getOwnerType = (ownerUrn?: string): OwnerType => {
  if (!ownerUrn) {
    throw new Error("OwnerUrn is missing");
  }
  if (ownerUrn.includes("user")) {
    return "user";
  }
  if (ownerUrn.includes("organisation")) {
    return "organisation";
  }
  throw new Error(`Invalid ownerUrn - must contain 'user' or 'organisation':  ${ownerUrn}`);
};

export const isElectric = (ad: CarAd): boolean => {
  const electicIds = [4, 6, 8];
  return electicIds.includes(ad?.engine?.fuel?.id ?? -1);
};
